import { useState } from 'react';

interface HeroSectionProps {
  onCtaClick: () => void;
}

export default function HeroSection({ onCtaClick }: HeroSectionProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.email || !formData.phone) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        firstName: formData.firstName,
        email: formData.email,
        phone: formData.phone
      });

      const response = await fetch('https://readdy.ai/api/form/d6ke1s2gaju8afbceol0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', email: '', phone: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative h-screen min-h-[800px] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/6abcb82eefd8f1115ce146de72e2ecd5.jpg"
          alt="Endurance athlete"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JhaW4iIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIj48ZmlsdGVyIGlkPSJub2lzZSI+PGZlVHVyYnVsZW5jZSB0eXBlPSJmcmFjdGFsTm9pc2UiIGJhc2VGcmVxdWVuY3k9IjAuOSIgbnVtT2N0YXZlcz0iNCIgLz48L2ZpbHRlcj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWx0ZXI9InVybCgjbm9pc2UpIiBvcGFjaXR5PSIwLjA1Ii8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyYWluKSIvPjwvc3ZnPg==')] opacity-30"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 lg:px-12 w-full">
        <div className="max-w-[900px]">
          {/* Headline */}
          <h1 className="text-[72px] lg:text-[96px] font-extrabold text-white leading-[0.95] tracking-tight mb-10">
            CROSS YOUR{' '}
            <span className="text-white" style={{ WebkitTextStroke: '2px #00FF88' }}>
              THRESHOLD
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl lg:text-2xl text-white/80 mb-5 max-w-[600px] leading-relaxed">
            The only endurance fuel system that adapts to every workout. Not a formula. A system.
          </p>

          {/* Product Line */}
          <p className="text-lg font-bold text-[#00FF88] tracking-[0.04em] mb-12">
            CARBS. ELECTROLYTES. CAFFEINE. — Built for Ironman. Dialed for every mile before it.
          </p>

          {/* Signup Form */}
          <form 
            onSubmit={handleSubmit} 
            className="mb-6"
            data-readdy-form
            id="hero-signup-form"
          >
            <div className="flex flex-col lg:flex-row gap-4 mb-4">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
                className="flex-1 lg:w-[200px] h-14 px-6 bg-black/80 text-white border border-white/30 rounded-lg placeholder:text-gray-400 focus:outline-none focus:border-[#00FF88] focus:ring-2 focus:ring-[#00FF88]/30 transition-all"
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="flex-1 lg:w-[200px] h-14 px-6 bg-black/80 text-white border border-white/30 rounded-lg placeholder:text-gray-400 focus:outline-none focus:border-[#00FF88] focus:ring-2 focus:ring-[#00FF88]/30 transition-all"
              />
              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="flex-1 lg:w-[200px] h-14 px-6 bg-black/80 text-white border border-white/30 rounded-lg placeholder:text-gray-400 focus:outline-none focus:border-[#00FF88] focus:ring-2 focus:ring-[#00FF88]/30 transition-all"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="whitespace-nowrap h-14 px-8 lg:w-[220px] bg-[#00FF88] text-black font-bold rounded-lg hover:scale-105 hover:shadow-[0_0_30px_rgba(0,255,136,0.5)] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSubmitting ? 'JOINING...' : 'GET EARLY ACCESS'}
              </button>
            </div>

            {submitStatus === 'success' && (
              <p className="text-[#00FF88] text-sm font-medium">Welcome to the team! Check your email for updates.</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-red-400 text-sm font-medium">Something went wrong. Please try again.</p>
            )}
          </form>

          {/* Trust Indicator */}
          <p className="text-sm text-gray-400 italic">
            Join 2,847 athletes already on the list
          </p>
        </div>
      </div>
    </section>
  );
}